import React, { useEffect, useRef, useState } from 'react';
import { AddressFind, DropLocation } from '@m4a3/api-client';
import { Obstacle } from '../../../models/obstacle.interface';

// LiDAR point interface to match the processed data from main component
interface LidarPoint {
  height: number; // Original AMSL height
  intensity: number;
  lat: number;
  lng: number;
  metresFromPoint: number;
  heightAGL: number; // Height Above Ground Level (AGL)
  heightAMSL: number; // Original AMSL height for reference
  distanceFromPoint: number;
  x?: number;
  y?: number;
}

interface GoogleMapProps {
  center: { lat: number; lng: number };
  zoom: number;
  onLoad?: (map: google.maps.Map) => void;
  onMarkerMove?: (position: { lat: number; lng: number }) => void;
  addressInfo?: AddressFind | null;
  obstacles: Obstacle[];
  geofences: any[];
  lidarData?: LidarPoint[];
  heightData?: number | null;
  columnWidth?: number;
  dropLocationHistory?: DropLocation[];
  selectedDropLocationId?: number | null;
  onDropLocationSelect?: (dropLocationId: number, lat: number, lng: number) => void;
  shouldShowDropLocation?: (dropLocation: DropLocation) => boolean;
  osmRiskData?: any;
  isLoadingOsmData?: boolean;
  buildingRisk?: string | null;
  isLoadingHeightData?: boolean;
  style?: React.CSSProperties;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  center,
  zoom,
  onLoad,
  onMarkerMove,
  addressInfo,
  obstacles,
  geofences,
  lidarData,
  heightData,
  columnWidth = 4,
  dropLocationHistory = [],
  selectedDropLocationId,
  onDropLocationSelect,
  shouldShowDropLocation,
  osmRiskData,
  isLoadingOsmData,
  buildingRisk,
  isLoadingHeightData,
  style
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [heightCircle, setHeightCircle] = useState<google.maps.Circle | null>(null);
  const [obstaclePolygons, setObstaclePolygons] = useState<google.maps.Polygon[]>([]);
  const [osmMarkers, setOsmMarkers] = useState<google.maps.Marker[]>([]);
  const [osmPolygons, setOsmPolygons] = useState<google.maps.Polygon[]>([]);

  // Obstacle color mapping based on ground risk
  const getObstacleColor = (groundRisk: number, isTimeActive: boolean = true): { fillColor: string; strokeColor: string; fillOpacity: number } => {
    const colors = {
      1: { fillColor: '#000000', strokeColor: '#000000' }, // Black
      2: { fillColor: '#FFC0CB', strokeColor: '#FF69B4' }, // Pink
      3: { fillColor: '#FF0000', strokeColor: '#CC0000' }, // Red
      4: { fillColor: '#800080', strokeColor: '#4B0082' }, // Purple
      5: { fillColor: '#FFA500', strokeColor: '#FF8C00' }, // Orange
      6: { fillColor: '#00FFFF', strokeColor: '#00CED1' }, // Cyan
      7: { fillColor: '#FF00FF', strokeColor: '#DA70D6' }, // Magenta
      8: { fillColor: '#0000FF', strokeColor: '#0066CC' }, // Blue
      9: { fillColor: '#00A1C9', strokeColor: '#007BA0' }, // Custom blue
      12: { fillColor: '#FFFFFF', strokeColor: '#CCCCCC' }, // White
    };
    
    const color = colors[groundRisk] || { fillColor: '#808080', strokeColor: '#666666' }; // Default gray
    
    return {
      ...color,
      fillOpacity: isTimeActive ? 0.4 : 0.1 // Reduce opacity for inactive time-based obstacles
    };
  };

  // Check if a time-based obstacle is currently active
  const isObstacleTimeActive = (obstacle: Obstacle): boolean => {
    const currentTime = Date.now() / 1000; // Convert to Unix timestamp
    
    // Check all features in the obstacle's geoJson
    const hasActiveFeatures = obstacle.geoJson?.features?.some(feature => {
      const timeStart = feature.properties?.timeStart;
      const timeEnd = feature.properties?.timeEnd;
      
      // If no time properties, consider it always active (traditional obstacle)
      if (!timeStart || !timeEnd) return true;
      
      return currentTime >= timeStart && currentTime <= timeEnd;
    });
    
    return hasActiveFeatures || false;
  };

  // Format time for display
  const formatTime = (timestamp: number): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleString('en-IE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get circle color based on building risk API and geo/height API response
  const getCircleColor = (height?: number | null): { stroke: string; fill: string; strokeWeight: number } => {
    // Show white circle while loading either assessment
    if (isLoadingHeightData) {
      return { stroke: '#FFFFFF', fill: '#FFFFFF', strokeWeight: 3 }; // White while loading
    }

    // Border color based on building risk evaluation
    let strokeColor = '#888888'; // Default gray
    if (buildingRisk === 'HIGH') {
      strokeColor = '#DC2626'; // Red border for near building
    } else if (buildingRisk === 'LOW') {
      strokeColor = '#10B981'; // Green border for clear area
    }

    // Background color based on height data (independent of building risk)
    let fillColor = '#888888'; // Default gray for no data/unknown
    if (height !== null && height !== undefined && !isNaN(height)) {
      if (height >= 1.0) {
        fillColor = '#DC2626'; // Red background for unsafe (obstacles detected ≥ 1.0m)
      } else {
        fillColor = '#10B981'; // Green background for safe (< 1.0m height)
      }
    }

    return { stroke: strokeColor, fill: fillColor, strokeWeight: 3 };
  };

  // Initialize the map
  useEffect(() => {
    if (mapRef.current && !map) {
      // Define Manna custom map type
      const mannaMapType = new google.maps.ImageMapType({
        getTileUrl: (coord, zoom) => {
          return `https://tiles.manna.aero/${zoom}/${coord.x}/${coord.y}.png`;
        },
        tileSize: new google.maps.Size(256, 256),
        maxZoom: 23,
        minZoom: 8,
        name: 'Manna'
      });

      const googleMap = new google.maps.Map(mapRef.current, {
        center,
        zoom,
        maxZoom: 23,
        mapTypeId: 'manna', // Use custom Manna tiles as default
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_CENTER,
          mapTypeIds: ['manna', google.maps.MapTypeId.SATELLITE, google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.HYBRID]
        },
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        streetViewControl: true,
        streetViewControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.RIGHT_TOP
        }
      });

      // Register the custom Manna map type
      googleMap.mapTypes.set('manna', mannaMapType);

      setMap(googleMap);
      if (onLoad) {
        onLoad(googleMap);
      }
    }
  }, [mapRef.current, center, zoom, onLoad]);

  // Create draggable marker
  useEffect(() => {
    if (map && !marker) {
      const newMarker = new google.maps.Marker({
        position: center,
        map: map,
        draggable: true,
        title: 'Drag me to analyze different locations',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
              <path fill="#4285F4" stroke="white" stroke-width="2" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          `)
        }
      });

      // Listen for marker drag events
      newMarker.addListener('dragend', () => {
        console.log('🎯 Marker dragend event fired');
        const position = newMarker.getPosition();
        if (position && onMarkerMove) {
          const newPos = { lat: position.lat(), lng: position.lng() };
          console.log('📍 Calling onMarkerMove with:', newPos);
          onMarkerMove(newPos);
        } else {
          console.log('❌ No position or onMarkerMove callback');
        }
      });

      // Also listen for map clicks to move marker
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const newPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
          console.log('🗺️ Map clicked, moving marker to:', newPos);
          newMarker.setPosition(newPos);
          if (onMarkerMove) {
            console.log('📍 Calling onMarkerMove from map click');
            onMarkerMove(newPos);
          }
        }
      });

      setMarker(newMarker);
    }
  }, [map, marker, center, onMarkerMove]);

  // Create analysis circle
  useEffect(() => {
    if (map && marker && !heightCircle) {
      const colors = getCircleColor(heightData);
      const circle = new google.maps.Circle({
        strokeColor: colors.stroke,
        strokeOpacity: 0.8,
        strokeWeight: colors.strokeWeight,
        fillColor: colors.fill,
        fillOpacity: 0.2,
        map: map,
        center: center,
        radius: columnWidth // Circle radius matches LiDAR column width
      });

      setHeightCircle(circle);
    }
  }, [map, marker, heightCircle, center, heightData, columnWidth]);

  // Update circle color, position and radius when height data, position or column width changes
  useEffect(() => {
    if (heightCircle) {
      const colors = getCircleColor(heightData);
      console.log('🎨 Updating circle with:', { 
        heightData, 
        colors, 
        center, 
        columnWidth 
      });
      heightCircle.setOptions({
        strokeColor: colors.stroke,
        strokeWeight: colors.strokeWeight,
        fillColor: colors.fill,
        center: center,
        radius: columnWidth
      });
    } else {
      console.log('❌ No heightCircle to update');
    }
  }, [heightCircle, heightData, center, columnWidth, buildingRisk, isLoadingHeightData]);

  // Update marker position when center changes
  useEffect(() => {
    if (marker && center) {
      marker.setPosition(center);
    }
  }, [marker, center]);

  // Add eircode reference marker (small, non-draggable)
  useEffect(() => {
    if (map && addressInfo) {
      const eircodeMarker = new google.maps.Marker({
        position: { lat: addressInfo.address_latitude, lng: addressInfo.address_longitude },
        map: map,
        title: `Eircode: ${addressInfo.eircode}`,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 50" width="40" height="50">
              <!-- Outer glow/shadow -->
              <ellipse cx="20" cy="46" rx="8" ry="3" fill="rgba(0,0,0,0.3)"/>
              
              <!-- Main pin body -->
              <path d="M20 2 C28 2 35 9 35 17 C35 25 20 45 20 45 C20 45 5 25 5 17 C5 9 12 2 20 2 Z" 
                    fill="#FF4444" stroke="white" stroke-width="3"/>
              
              <!-- Inner circle -->
              <circle cx="20" cy="17" r="8" fill="white"/>
              
              <!-- Eircode text -->
              <text x="20" y="22" text-anchor="middle" fill="#FF4444" font-size="10" font-weight="bold" font-family="Arial">E</text>
              
              <!-- Pulsing ring animation -->
              <circle cx="20" cy="17" r="12" fill="none" stroke="#FF4444" stroke-width="2" opacity="0.6">
                <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
              </circle>
            </svg>
          `),
          scaledSize: new google.maps.Size(40, 50),
          anchor: new google.maps.Point(20, 45)
        }
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div>
            <h3>📍 Eircode Location</h3>
            <p><strong>Eircode:</strong> ${addressInfo.eircode}</p>
            <p><strong>Address:</strong> ${addressInfo.address}</p>
            <p><em>Drag the blue marker to analyze different locations</em></p>
          </div>
        `
      });

      eircodeMarker.addListener('click', () => {
        infoWindow.open(map, eircodeMarker);
      });

      return () => {
        eircodeMarker.setMap(null);
      };
    }
  }, [map, addressInfo]);

  // Add drop location history markers
  useEffect(() => {
    if (map && dropLocationHistory && dropLocationHistory.length > 0) {
      const historyMarkers: google.maps.Marker[] = [];
      const infoWindows: google.maps.InfoWindow[] = [];
      
      dropLocationHistory
        .filter(shouldShowDropLocation || ((dropLocation) => !dropLocation.suggestedForAddressFindId))
        .forEach((dropLocation, index) => {
          const isSelected = selectedDropLocationId === dropLocation.id;
          const deliveryCount = dropLocation.okDeliveries || 0;
          const riskLevel = dropLocation.risk || 'UNKNOWN';
          
          const historyMarker = new google.maps.Marker({
            position: { lat: dropLocation.latitude, lng: dropLocation.longitude },
            map: map,
            title: `Drop Location #${dropLocation.id} - ${deliveryCount} deliveries`,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20">
                  <circle cx="10" cy="10" r="8" fill="${isSelected ? '#3B82F6' : '#6B7280'}" stroke="white" stroke-width="2"/>
                  <text x="10" y="13" text-anchor="middle" fill="white" font-size="8" font-weight="bold">${Math.min(deliveryCount, 9)}</text>
                </svg>
              `)
            },
            zIndex: isSelected ? 1000 : 500
          });

          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="min-width: 200px;">
                <h3>📍 Drop Location History</h3>
                <p><strong>Deliveries:</strong> ${deliveryCount}</p>
                <p><strong>Risk Level:</strong> ${riskLevel}</p>
                <p><strong>Created:</strong> ${new Date(dropLocation.createDate).toLocaleDateString()}</p>
                <p><strong>Coordinates:</strong> ${dropLocation.latitude.toFixed(6)}, ${dropLocation.longitude.toFixed(6)}</p>
                ${dropLocation.isBackGardenApproved ? '<p>✓ Back garden approved</p>' : ''}
                ${dropLocation.userCreated ? '<p>✓ User created</p>' : ''}
                <button onclick="window.selectDropLocation(${dropLocation.id}, ${dropLocation.latitude}, ${dropLocation.longitude})" 
                        style="background: #3B82F6; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-top: 8px;">
                  Select This Location
                </button>
              </div>
            `
          });

          historyMarker.addListener('click', () => {
            // Close all other info windows
            infoWindows.forEach(iw => iw.close());
            infoWindow.open(map, historyMarker);
          });

          historyMarkers.push(historyMarker);
          infoWindows.push(infoWindow);
        });

             // Add global function for button clicks and direct marker clicks
       (window as any).selectDropLocation = (dropLocationId: number, lat: number, lng: number) => {
         if (onDropLocationSelect) {
           onDropLocationSelect(dropLocationId, lat, lng);
         }
         // Close all info windows
         infoWindows.forEach(iw => iw.close());
       };

       // Also add click listeners to markers for direct selection
       dropLocationHistory
         .filter(shouldShowDropLocation || ((dropLocation) => !dropLocation.suggestedForAddressFindId))
         .forEach((dropLocation, index) => {
           const marker = historyMarkers[index];
           marker.addListener('dblclick', () => {
             if (onDropLocationSelect) {
               onDropLocationSelect(dropLocation.id, dropLocation.latitude, dropLocation.longitude);
             }
             // Close all info windows
             infoWindows.forEach(iw => iw.close());
           });
         });

      return () => {
        historyMarkers.forEach(marker => marker.setMap(null));
        infoWindows.forEach(iw => iw.close());
        delete (window as any).selectDropLocation;
      };
    }
  }, [map, dropLocationHistory, selectedDropLocationId, onDropLocationSelect]);

  // Add obstacle rendering
  useEffect(() => {
    if (map && obstacles && obstacles.length > 0) {
      console.log('🗺️ Rendering obstacles:', obstacles.length, 'obstacles found');
      console.log('📍 First obstacle sample:', obstacles[0]);
      
      // Clear existing obstacle polygons
      obstaclePolygons.forEach(polygon => {
        polygon.setMap(null);
      });
      
      const newPolygons: google.maps.Polygon[] = [];
      
      obstacles.forEach((obstacle, obstacleIndex) => {
        console.log(`🛑 Processing obstacle ${obstacleIndex + 1}:`, obstacle.name, obstacle.geoJson);
        
        // Handle different obstacle data structures
        let features: any[] = [];
        
        if (obstacle.geoJson?.features) {
          // Standard FeatureCollection format
          features = obstacle.geoJson.features;
          console.log(`📊 Obstacle ${obstacle.id} has FeatureCollection with ${features.length} features`);
        } else if ((obstacle.geoJson as any)?.type === 'Feature') {
          // Single Feature format
          features = [obstacle.geoJson];
          console.log(`📊 Obstacle ${obstacle.id} is a single Feature`);
        } else {
          // Check if obstacle itself is a feature (for time-based obstacles)
          const obstacleAsAny = obstacle as any;
          if (obstacleAsAny.type === 'Feature' && obstacleAsAny.geometry) {
            features = [obstacleAsAny];
            console.log(`📊 Obstacle ${obstacle.id} is itself a Feature object`);
          } else {
            console.warn(`⚠️ Obstacle ${obstacle.id} has no valid geoJson data:`, obstacle);
            return;
          }
        }
        
        if (features.length === 0) {
          console.warn(`⚠️ Obstacle ${obstacle.id} has no features to render`);
          return;
        }
        
        const isTimeActive = isObstacleTimeActive(obstacle);
        const colors = getObstacleColor(obstacle.groundRisk, isTimeActive);
        
        console.log(`🎨 Obstacle ${obstacle.id} colors:`, colors, 'isTimeActive:', isTimeActive);
        
        features.forEach((feature, featureIndex) => {
          console.log(`📐 Feature ${featureIndex + 1} geometry:`, feature.geometry?.type, feature.geometry);
          
          if (feature.geometry?.type === 'Polygon') {
            const polygonGeometry = feature.geometry as any; // Type assertion for GeoJSON Polygon
            if (polygonGeometry.coordinates) {
              try {
                // Convert coordinates for Google Maps (reverse lat/lng order)
                const paths = polygonGeometry.coordinates.map((ring: number[][]) => 
                  ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
                );
              
              console.log(`📍 Converted paths for obstacle ${obstacle.id}:`, paths);
              
              const polygon = new google.maps.Polygon({
                paths: paths,
                strokeColor: colors.strokeColor,
                strokeOpacity: 0.8,
                strokeWeight: 3, // Increased for better visibility
                fillColor: colors.fillColor,
                fillOpacity: Math.max(0.3, colors.fillOpacity), // Ensure minimum visibility
                map: map,
                zIndex: 1000 // Ensure polygons appear above other map elements
              });
              
              console.log(`✅ Created polygon for obstacle ${obstacle.id}, feature ${featureIndex + 1}`);
              
              // Create info window content
              const createInfoContent = () => {
                const timeStart = feature.properties?.timeStart;
                const timeEnd = feature.properties?.timeEnd;
                const altitudeLower = feature.properties?.altitudeLower;
                const altitudeUpper = feature.properties?.altitudeUpper;
                const isTimeBased = timeStart && timeEnd;
                
                let statusIcon = '⚠️';
                let statusText = 'Traditional Obstacle';
                
                if (isTimeBased) {
                  if (isTimeActive) {
                    statusIcon = '🔴';
                    statusText = 'ACTIVE NOW';
                  } else {
                    statusIcon = '🟡';
                    statusText = 'Inactive (Time-based)';
                  }
                }
                
                return `
                  <div style="min-width: 250px; font-family: Arial, sans-serif;">
                    <h3 style="margin: 0 0 8px 0; color: #1F2937; font-size: 16px;">
                      ${statusIcon} ${obstacle.name || 'Obstacle'}
                    </h3>
                    
                    <div style="margin-bottom: 8px;">
                      <strong>Status:</strong> 
                      <span style="color: ${isTimeActive ? '#DC2626' : '#6B7280'}; font-weight: bold;">
                        ${statusText}
                      </span>
                    </div>
                    
                    <div style="margin-bottom: 8px;">
                      <strong>Risk Level:</strong> ${obstacle.groundRisk}
                    </div>
                    
                    ${altitudeLower !== undefined && altitudeUpper !== undefined ? `
                      <div style="margin-bottom: 8px;">
                        <strong>Altitude Range:</strong> ${altitudeLower}m - ${altitudeUpper}m
                      </div>
                    ` : ''}
                    
                    ${isTimeBased ? `
                      <div style="margin-bottom: 8px; padding: 8px; background-color: #F3F4F6; border-radius: 4px;">
                        <div style="font-weight: bold; margin-bottom: 4px; color: #374151;">⏰ Time Window:</div>
                        <div style="font-size: 12px; color: #6B7280;">
                          <strong>Start:</strong> ${formatTime(timeStart)}<br>
                          <strong>End:</strong> ${formatTime(timeEnd)}
                        </div>
                      </div>
                    ` : ''}
                    
                    ${obstacle.note ? `
                      <div style="margin-top: 8px; padding: 6px; background-color: #FEF3C7; border-left: 3px solid #F59E0B; font-size: 12px;">
                        <strong>Note:</strong> ${obstacle.note}
                      </div>
                    ` : ''}
                    
                    <div style="margin-top: 12px; font-size: 10px; color: #9CA3AF; border-top: 1px solid #E5E7EB; padding-top: 6px;">
                      Obstacle ID: ${obstacle.id}<br>
                      Feature: ${featureIndex + 1} of ${obstacle.geoJson.features.length}
                    </div>
                  </div>
                `;
              };
              
              const infoWindow = new google.maps.InfoWindow({
                content: createInfoContent()
              });
              
              polygon.addListener('click', (event) => {
                console.log('🖱️ Polygon clicked for obstacle:', obstacle.id);
                // Close any open info windows
                infoWindow.close();
                infoWindow.setPosition(event.latLng);
                infoWindow.open(map);
              });
              
              newPolygons.push(polygon);
              
                          } catch (error) {
                console.error(`❌ Error creating polygon for obstacle ${obstacle.id}:`, error);
              }
            } else {
              console.warn(`⚠️ Obstacle ${obstacle.id} feature ${featureIndex + 1} - missing coordinates`);
            }
          } else {
            console.warn(`⚠️ Skipping feature ${featureIndex + 1} - not a polygon:`, feature.geometry?.type);
          }
        });
      });
      
      console.log(`✅ Created ${newPolygons.length} obstacle polygons total`);
      setObstaclePolygons(newPolygons);
    } else {
      console.log('🗺️ No obstacles to render:', { map: !!map, obstacles: obstacles?.length });
    }
  }, [map, obstacles]);

  // Periodic update for time-based obstacles
  useEffect(() => {
    if (!map || !obstacles || obstacles.length === 0) return;
    
    // Check if we have any time-based obstacles
    const hasTimeBasedObstacles = obstacles.some(obstacle => 
      obstacle.geoJson?.features?.some(feature => 
        feature.properties?.timeStart && feature.properties?.timeEnd
      )
    );
    
    if (!hasTimeBasedObstacles) return;
    
    // Set up periodic refresh every 30 seconds for time-based obstacles
    const interval = setInterval(() => {
      // Force re-render by updating obstacle polygons
      setObstaclePolygons(current => {
        // Clear existing polygons
        current.forEach(polygon => polygon.setMap(null));
        return [];
      });
      
      // Trigger re-render by forcing the obstacles effect to run again
      // This will be handled by the dependency on obstacles in the main effect
    }, 30000);
    
    return () => clearInterval(interval);
  }, [map, obstacles]);

  // Add OSM risk data rendering
  useEffect(() => {
    if (map && osmRiskData) {
      console.log('🏠 Rendering OSM risk data:', osmRiskData);
      
      // Clear existing OSM markers and polygons
      osmMarkers.forEach(marker => marker.setMap(null));
      osmPolygons.forEach(polygon => polygon.setMap(null));
      
      const newMarkers: google.maps.Marker[] = [];
      const newPolygons: google.maps.Polygon[] = [];
      
      // Handle OSM data - it might be a FeatureCollection or array of features
      let features: any[] = [];
      
      if (osmRiskData.features) {
        features = osmRiskData.features;
      } else if (Array.isArray(osmRiskData)) {
        features = osmRiskData;
      } else {
        console.warn('⚠️ Unexpected OSM data format:', osmRiskData);
        return;
      }
      
      console.log(`🗺️ Processing ${features.length} OSM features`);
      
      features.forEach((feature, index) => {
        if (!feature.geometry) {
          console.warn(`⚠️ OSM feature ${index} has no geometry`);
          return;
        }
        
        try {
          if (feature.geometry.type === 'Point') {
            // Create marker for point features
            const coords = feature.geometry.coordinates;
            const position = { lat: coords[1], lng: coords[0] };
            
            const marker = new google.maps.Marker({
              position: position,
              map: map,
              title: feature.properties?.name || feature.properties?.type || 'OSM Feature',
              icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20">
                    <circle cx="10" cy="10" r="8" fill="#FF6B6B" stroke="white" stroke-width="2"/>
                    <text x="10" y="13" text-anchor="middle" fill="white" font-size="10" font-weight="bold">!</text>
                  </svg>
                `),
                scaledSize: new google.maps.Size(20, 20),
                anchor: new google.maps.Point(10, 10)
              }
            });
            
            // Create info window for point
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div style="min-width: 200px; font-family: Arial, sans-serif;">
                  <h3 style="margin: 0 0 8px 0; color: #1F2937; font-size: 14px;">
                    🏠 ${feature.properties?.name || 'OSM Risk Feature'}
                  </h3>
                  <div style="margin-bottom: 6px;">
                    <strong>Type:</strong> ${(feature.properties?.type || 'Unknown').replace(/_/g, ' ')}
                  </div>
                  ${feature.properties?.name ? `
                    <div style="margin-bottom: 6px;">
                      <strong>Name:</strong> ${feature.properties.name}
                    </div>
                  ` : ''}
                  <div style="margin-top: 8px; font-size: 10px; color: #9CA3AF; border-top: 1px solid #E5E7EB; padding-top: 6px;">
                    OSM Risk Data • Coordinates: ${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}
                  </div>
                </div>
              `
            });
            
            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });
            
            newMarkers.push(marker);
            
          } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            // Create polygon for area features
            let coordinates = feature.geometry.coordinates;
            
            // Handle MultiPolygon by taking the first polygon
            if (feature.geometry.type === 'MultiPolygon') {
              coordinates = coordinates[0];
            }
            
            const paths = coordinates.map((ring: number[][]) => 
              ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
            );
            
            const polygon = new google.maps.Polygon({
              paths: paths,
              strokeColor: '#FF6B6B',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#FF6B6B',
              fillOpacity: 0.2,
              map: map,
              zIndex: 500 // Lower than obstacles
            });
            
            // Create info window for polygon
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div style="min-width: 200px; font-family: Arial, sans-serif;">
                  <h3 style="margin: 0 0 8px 0; color: #1F2937; font-size: 14px;">
                    🏠 ${feature.properties?.name || 'OSM Risk Area'}
                  </h3>
                  <div style="margin-bottom: 6px;">
                    <strong>Type:</strong> ${(feature.properties?.type || 'Unknown').replace(/_/g, ' ')}
                  </div>
                  ${feature.properties?.name ? `
                    <div style="margin-bottom: 6px;">
                      <strong>Name:</strong> ${feature.properties.name}
                    </div>
                  ` : ''}
                  <div style="margin-top: 8px; font-size: 10px; color: #9CA3AF; border-top: 1px solid #E5E7EB; padding-top: 6px;">
                    OSM Risk Area • ${feature.geometry.type}
                  </div>
                </div>
              `
            });
            
            polygon.addListener('click', (event) => {
              infoWindow.setPosition(event.latLng);
              infoWindow.open(map);
            });
            
            newPolygons.push(polygon);
          }
          
        } catch (error) {
          console.error(`❌ Error rendering OSM feature ${index}:`, error, feature);
        }
      });
      
      console.log(`✅ Created ${newMarkers.length} OSM markers and ${newPolygons.length} OSM polygons`);
      setOsmMarkers(newMarkers);
      setOsmPolygons(newPolygons);
      
    } else if (map && osmRiskData === null) {
      // Clear OSM data when none available
      osmMarkers.forEach(marker => marker.setMap(null));
      osmPolygons.forEach(polygon => polygon.setMap(null));
      setOsmMarkers([]);
      setOsmPolygons([]);
    }
  }, [map, osmRiskData]);

  // Cleanup obstacle polygons on unmount
  useEffect(() => {
    return () => {
      obstaclePolygons.forEach(polygon => {
        polygon.setMap(null);
      });
    };
  }, [obstaclePolygons]);

  // Cleanup OSM data on unmount
  useEffect(() => {
    return () => {
      osmMarkers.forEach(marker => marker.setMap(null));
      osmPolygons.forEach(polygon => polygon.setMap(null));
    };
  }, [osmMarkers, osmPolygons]);

  // Cleanup circle on unmount
  useEffect(() => {
    return () => {
      if (heightCircle) {
        heightCircle.setMap(null);
      }
    };
  }, [heightCircle]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '512px',
        ...style
      }}
    />
  );
};

export default GoogleMap;